import {
  ArrowLeftOutlined,
  FilePdfOutlined,
  FontSizeOutlined,
  HistoryOutlined,
  PictureOutlined,
} from '@ant-design/icons'
import ActivityLog from '@components/activityLog/ActivityLog'
import BookletUploader from '@components/BookletUploader'
import DemoTabPhotoUploader from '@components/DemoTabPhotoUploader'
import DemoTabTextsEditor from '@components/DemoTabTextsEditor'
import Page from '@components/Page/Page'
import {
  DemoBooklet,
  fetchDemoBooklet,
  updateDemoBooklet,
} from '@services/MembershipBookletService'
import {
  DemoTabPhoto,
  fetchDemoTabPhoto,
  updateDemoTabPhoto,
} from '@services/MembershipDemoTabPhotoService'
import {
  DemoTabTexts,
  fetchDemoTabTexts,
  updateDemoTabTexts,
} from '@services/MembershipDemoTabTextsService'
import { Permission } from '@utils/permissions'
import { Button, Col } from 'antd'
import { useEffect, useState } from 'react'

const DEMO_BOOKLET_LOG_NAME = 'membership-demo-booklet'
const DEMO_TAB_TEXTS_LOG_NAME = 'membership-demo-tab-texts'
const DEMO_TAB_PHOTO_LOG_NAME = 'membership-demo-tab-photo'
const LOG_NAMES = [
  DEMO_BOOKLET_LOG_NAME,
  DEMO_TAB_TEXTS_LOG_NAME,
  DEMO_TAB_PHOTO_LOG_NAME,
]

/**
 * What app users with no active membership see on the membership demo tab: the
 * demo booklet, the tab's title and intro text, and its photo. Members see their
 * own program's booklet, uploaded on the program's Design & branding tab.
 */
const MembershipBookletsPage = () => {
  const [booklet, setBooklet] = useState<DemoBooklet>({})
  // `undefined` until loaded: the texts editor stays disabled meanwhile.
  const [texts, setTexts] = useState<DemoTabTexts['texts'] | undefined>()
  const [textsLoadError, setTextsLoadError] = useState<string>()
  const [photo, setPhoto] = useState<DemoTabPhoto['photo']>(null)
  // The activity log takes the place of the three sections while it is shown.
  const [showActivityLog, setShowActivityLog] = useState<boolean>(false)
  // A plain `hidden` loses to the display antd gives every sized column.
  const sectionStyle = showActivityLog ? { display: 'none' } : undefined

  const loadBooklet = () =>
    fetchDemoBooklet().then(({ data }) => setBooklet(data))
  const loadTexts = () =>
    fetchDemoTabTexts().then(({ data }) => {
      setTexts(data.texts ?? null)
      setTextsLoadError(undefined)
    })
  const loadPhoto = () =>
    fetchDemoTabPhoto().then(({ data }) => setPhoto(data.photo))

  useEffect(() => {
    loadBooklet()
    loadTexts().catch(() =>
      setTextsLoadError(
        'The current texts could not be loaded. Reload the page to edit them.'
      )
    )
    loadPhoto()
  }, [])

  return (
    <Page
      permissions={Permission.manageSettingsMembershipSettings}
      pageTitle={{
        title: 'Membership demo tab',
        level: 4,
        toolbar: (
          <Button
            icon={showActivityLog ? <ArrowLeftOutlined /> : <HistoryOutlined />}
            onClick={() => setShowActivityLog((shown) => !shown)}
          >
            {showActivityLog ? 'Back to settings' : 'Activity log'}
          </Button>
        ),
      }}
      noWrapWithCol
    >
      {showActivityLog && (
        <Col span={24}>
          <ActivityLog.Timeline logName={LOG_NAMES} />
        </Col>
      )}
      {/* Hidden, not unmounted, so unsaved edits survive a look at the log. */}
      <Col span={24} style={sectionStyle}>
        <BookletUploader
          title={
            <>
              <FilePdfOutlined /> Demo booklet
            </>
          }
          description="Shown to app users who are not members yet. Members get the booklet of their own program, set on the program's Design & branding tab."
          current={booklet}
          onSubmit={(files) =>
            updateDemoBooklet(files).then(() => {
              loadBooklet()
            })
          }
        />
      </Col>
      <Col span={12} style={sectionStyle}>
        <DemoTabTextsEditor
          title={
            <>
              <FontSizeOutlined /> Demo tab texts
            </>
          }
          description="Shown to app users who are not members yet: the title above the intro text, which sits above the photo. An empty field makes the app show its built-in title or intro text for that language. The navigation bar title (Paris Society Membership) is not affected."
          current={texts}
          loadError={textsLoadError}
          onSubmit={(values) =>
            // A failed reload is not a failed save.
            updateDemoTabTexts(values).then(() =>
              loadTexts().catch(() => undefined)
            )
          }
        />
      </Col>
      <Col span={12} style={sectionStyle}>
        <DemoTabPhotoUploader
          title={
            <>
              <PictureOutlined /> Demo tab photo
            </>
          }
          description="Shown at the top of the membership tab to app users who are not members yet, in every language. It is cropped to the ratio of the app's banner before saving, and can be replaced but not removed."
          current={photo}
          onSubmit={(file) =>
            // Resolve once the new photo is loaded, so the uploader does not
            // flash the replaced one; a failed reload is not a failed save.
            updateDemoTabPhoto(file).then(() =>
              loadPhoto().catch(() => undefined)
            )
          }
        />
      </Col>
    </Page>
  )
}

export default MembershipBookletsPage
